<template>
    <!-- eslint-disable vue/no-v-html -->
    <div
        ref="stepPreview"
        class="guide-editable-step-preview step-preview"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave">
        <div
            ref="preview"
            class="preview"
            :class="{
                'is-mobile-preview': isMobile,
            }"
            v-html="previewHtml"
            @input="handleInput" />
        <div
            v-if="showAddStepButton"
            class="step-actions-container">
            <add-step-button @click="$emit('open-layout-chooser')" />
        </div>
        <guides-conversational-ai-modal
            v-if="stepOptionsModal.visible"
            :visible="stepOptionsModal.visible"
            :target-element="stepOptionsModal.targetElement"
            :component="stepOptionsModal.component">
            <template #toolbarItems>
                <pendo-icon-button
                    v-pendo-tooltip="duplicateStepButtonTooltip"
                    class="ai-toolbar-floating__copy-btn"
                    icon="copy"
                    icon-size="16"
                    @click="$emit('copy-step')" />
                <pendo-icon-button
                    v-pendo-tooltip="deleteStepButtonTooltip"
                    class="ai-toolbar-floating__trash-btn"
                    icon="trash-2"
                    icon-size="16"
                    @click="$emit('delete-step')" />
            </template>
        </guides-conversational-ai-modal>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { PendoIconButton, PendoTooltip } from '@pendo/components';
import morphdom from 'morphdom';
import { GuideStepPreviewUtils } from '@pendo/one-ui-shared';
import {
    makeElementEditable,
    getBestOutlineColor,
    EDITABLE_CONTENT_TYPE,
    PENDO_EDITING_ID_ATTRIBUTE,
    EDITABLE_ELEMENTS_SELECTOR,
    DEFAULT_STEP_Z_INDEX,
} from './editable-elements.utils';
import { BuildingBlock } from '@pendo/services/BuildingBlocks';
import GuidesConversationalAiModal from './guides-conversational-ai-modal.vue';
import AddStepButton from './add-step-button.vue';

const {
    buildPreview,
    handleGuideImagesLoading,
    resizeGuide,
    resizePreview,
    shouldAdjustContentWidth,
} = GuideStepPreviewUtils;

const emptyPreview = '<div style="width:100%;height:100%"></div>';
const EDITABLE_CONTENT_CLASS = 'pendo-guide-editable-content';

export default {
    name: 'GuideEditableStepPreview',
    components: {
        AddStepButton,
        GuidesConversationalAiModal,
        PendoIconButton,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        dom: {
            type: String,
            default: '',
        },
        watermarkDom: {
            type: String,
            default: '',
        },
        guides: {
            type: Array,
            default: null,
        },
        step: {
            type: Object,
            default: null,
        },
        backgroundColor: {
            type: String,
            default: null,
        },
        isMobile: {
            type: Boolean,
            default: false,
        },
        hasGuideLocalization: {
            type: Boolean,
            default: false,
        },
        debounceTime: {
            type: Number,
            default: 16,
        },
        stepIndex: {
            type: Number,
            default: null,
        },
    },
    emits: [
        'delete-step',
        'open-ai-modal',
        'content-block-update',
        'toggle-undo-button',
        'copy-step',
        'open-layout-chooser',
    ],
    data () {
        return {
            guideWidth: 0,
            guideHeightData: null,
            height: 0,
            width: 0,
            previewHtml: null,
            imagesInGuide: [],
            stepOptionsModal: {
                visible: false,
                component: 'toolbar',
                targetElement: null,
            },
            showAddStepButton: false,
        };
    },
    computed: {
        shouldAdjustContentWidth () {
            return shouldAdjustContentWidth(this.guides, false);
        },
        guideContainer () {
            return get(this.$refs, 'preview.firstChild.children[0]');
        },
    },
    watch: {
        guides: {
            async handler (newGuides, oldGuides) {
                const newGuide = newGuides[0];
                const oldGuide = oldGuides[0];
                // Handles the case for announcement guides that display the start date.
                // attributes.date is updated when the start date is changed. For localized guides,
                // this makes sure the preview is updated when this value changes.
                if (get(newGuide, 'attributes.dates') !== get(oldGuide, 'attributes.dates')) {
                    this.buildAndResizePreview();
                }
            },
        },
    },
    mounted () {
        this.onResize = debounce(() => {
            this.width = this.$refs.stepPreview.offsetWidth;
            this.height = this.$refs.stepPreview.offsetHeight;
            this.updateGuideSize();
        }, this.debounceTime);

        window.addEventListener('resize', this.onResize);
        window.addEventListener('focus', this.onResize);

        this.buildAndResizePreview();

        this.deleteStepButtonTooltip = this.$t('guidesEditableStepPreview.deleteStepButtonTooltip');
        this.duplicateStepButtonTooltip = this.$t('guidesEditableStepPreview.duplicateStepButtonTooltip');
    },
    beforeUnmount () {
        this.onResize.cancel();
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('focus', this.onResize);

        this.imagesInGuide.forEach((img) => {
            img.removeEventListener('load', this.handleImageLoad);
            img.removeEventListener('error', this.handleImageLoad);
        });
    },
    methods: {
        handleMouseEnter () {
            this.stepOptionsModal.targetElement = this.$refs.preview;
            this.stepOptionsModal.visible = true;
            this.showAddStepButton = true;
        },
        handleMouseLeave () {
            this.stepOptionsModal.visible = false;
            this.showAddStepButton = false;
        },
        setOutlineColor () {
            const stepBackgroundRgbString = this.guideContainer.style.backgroundColor;
            this.$refs.stepPreview.style.setProperty('--outline-color', getBestOutlineColor(stepBackgroundRgbString));
        },
        getEditableElements () {
            if (!this.guideContainer) return [];

            return this.guideContainer.querySelectorAll(EDITABLE_ELEMENTS_SELECTOR);
        },
        emitContentUpdate (event) {
            const { currentTarget: dom } = event;
            const editingId = dom.getAttribute(PENDO_EDITING_ID_ATTRIBUTE);
            const type = dom.getAttribute(EDITABLE_CONTENT_TYPE);

            return this.$emit('content-block-update', {
                stepIndex: this.stepIndex,
                editingId,
                type,
                data: {
                    dom,
                },
            });
        },
        getElementTextContent (element) {
            const editableArea = element.querySelector('.pendo-guide-editable-content-area');

            return editableArea ? editableArea.textContent : element.textContent;
        },
        prepElementsToBeEditable () {
            this.setOutlineColor();
            const elements = this.getEditableElements();
            elements.forEach((element) => {
                makeElementEditable({ element, changeHandler: this.emitContentUpdate });
                this.setupElementInteractions(element);
            });
        },
        handleContentChange () {
            this.$emit('toggle-undo-button');
        },
        handleElementClick ({ event, element }) {
            event.preventDefault();
            event.stopPropagation();
            const editingId = element.getAttribute(PENDO_EDITING_ID_ATTRIBUTE);
            const targetElementBuildingBlock = BuildingBlock.findBlockByEditingId(this.step.buildingBlocks, editingId);

            this.$emit('open-ai-modal', {
                targetElement: element,
                stepIndex: this.stepIndex,
                targetElementOriginalText: this.getElementTextContent(element),
                targetElementBuildingBlock,
            });
        },
        setupElementInteractions (element) {
            element.addEventListener('click', (event) => this.handleElementClick({ event, element }));
            element.addEventListener('input', this.handleContentChange);
            element.addEventListener('keyup', this.handleContentChange);
        },
        handleInput () {
            this.onResize();
        },
        buildPreview () {
            return buildPreview(this.step, this.dom, this.guideHeightData, {
                isResourceCenterPreview: false,
                isMobile: this.isMobile,
                announcementsCountMap: {},
                guides: this.guides,
                backgroundColorOverride: this.backgroundColor,
                hasGuideLocalization: this.hasGuideLocalization,
                watermarkDom: this.watermarkDom,
                resourceCenters: [],
            });
        },
        async buildAndResizePreview () {
            const guideContainer = await this.buildPreview();
            guideContainer.style.zIndex = DEFAULT_STEP_Z_INDEX;
            this.previewHtml = guideContainer.outerHTML;

            await this.$nextTick();
            this.prepElementsToBeEditable();
            this.$refs.preview.style.visibility = 'hidden';
            const { imagesInGuide, handleImageLoad } = handleGuideImagesLoading(
                this.$refs.preview.firstChild,
                this.onResize,
            );
            this.imagesInGuide = imagesInGuide;
            this.handleImageLoad = handleImageLoad;
            if (!this.imagesInGuide.length) {
                this.onResize();
            }
        },
        async morphStepPreview (stepPreviewClone) {
            morphdom(this.$refs.stepPreview, stepPreviewClone, {
                getNodeKey: (node) => {
                    if (node.classList && node.classList.contains(EDITABLE_CONTENT_CLASS) && node.dataset.editingId) {
                        return node.dataset.editingId;
                    }

                    return node.id;
                },
            });
        },
        async handleResizeOnInput () {
            const stepPreviewClone = this.$refs.stepPreview.cloneNode(true);
            this.$refs.stepPreview.insertAdjacentElement('afterend', stepPreviewClone);

            window.pendo.flexElement(stepPreviewClone);

            await this.morphStepPreview(stepPreviewClone);
            stepPreviewClone.remove();
        },
        async updateGuideSize () {
            if (this.previewHtml === emptyPreview) {
                this.$refs.preview.style.visibility = 'visible';

                return;
            }

            const $guideBase = this.$refs.preview.firstChild;
            const { guideWidth, guideHeight } = await resizeGuide(
                $guideBase,
                this.$refs.stepPreview,
                this.step.buildingBlocks,
                this.handleResizeOnInput,
                this.shouldAdjustContentWidth,
            );
            if ($guideBase) {
                this.guideWidth = guideWidth;
                this.guideHeightData = guideHeight;
            }

            this.resizePreview();
        },
        resizePreview () {
            const $preview = this.$refs.preview;
            if (!this.width || !this.guideWidth || !this.guideHeightData || !$preview) {
                return;
            }
            const scale = this.calculateScale();
            const $guideContainer = $preview.firstChild;
            resizePreview($preview, $guideContainer, scale);
        },
        calculateScale () {
            return this.width / this.guideWidth;
        },
    },
};
</script>

<style lang="less" scoped>
.step-preview {
    position: relative;
    flex: 1;
    height: 250px;
    min-height: 250px;
    max-height: 250px;
    display: flex;
    flex-direction: column;
    align-items: center;

    :deep(> .preview) {
        ._pendo-close-guide {
            &:focus,
            &:hover {
                cursor: default;
            }
        }
    }

    &:hover {
        .preview {
            :deep(.pendo-tooltip-caret-border) {
                border-bottom-color: var(--outline-color) !important;
            }

            &::after {
                outline: 1px solid var(--outline-color);
            }
        }
    }
}

.step-preview > .preview {
    left: 50%;
    overflow: visible;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);

    &.is-mobile-preview :deep(#pendo-guide-container) {
        box-shadow: 0 5px 10px 0 var(--semantic-drop-shadow-base) !important;
    }

    &.is-mobile-preview {
        :deep(#pendo-guide-container) {
            margin: 0 !important;
        }
    }
}

.step-preview > .preview:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    pointer-events: none;
}

.step-preview > .preview :deep(._pendo-step-container-size) {
    transform-origin: left top;
    position: relative !important;
}

:deep(.pendo-guide-editable-content) {
    outline: none;

    .pendo-guide-editable-content-area {
        outline: none;
        height: 100%;
        width: 100%;
        display: block;
        white-space: nowrap;
    }

    &:focus,
    &:hover {
        cursor: text;
        outline: 1px solid var(--outline-color);
        outline-offset: 2px;
    }
}

.ai-toolbar-floating {
    &__trash-btn {
        border-radius: 3px 0 0 3px;
        border-right: 1px solid var(--palette-gray-40);
        color: var(--palette-gray-100);
        width: 28px;
        margin: 0px;
    }

    &__copy-btn {
        border-radius: 0 3px 3px 0;
        border-left: 1px solid var(--palette-gray-40);
        color: var(--palette-gray-100);
        width: 28px;
        margin: 0px;
    }
}

.step-actions-container {
    height: 0px;

    .add-step-btn {
        margin-top: 16px;
    }
}
</style>
